// Buddy Manifest — the single source of truth for "given this learner's buddy,
// what image should I show in this context?" (see Docs/Buddy-Service.md).
//
// Pure JS. No React, no imports. Every component that renders a buddy image
// resolves its path through the accessors below — nothing is hardcoded.
//
// To add a new pose: drop a numbered file in /public/assets/buddies/{slug}/
// and append its path to the relevant slot array. No code changes anywhere else.

export const SLOT = {
  AVATAR: 'AVATAR',
  FIGURINE_VIEW_1: 'FIGURINE_VIEW_1',
  FIGURINE_VIEW_2: 'FIGURINE_VIEW_2',
  CELEBRATE: 'CELEBRATE',
  ENCOURAGE: 'ENCOURAGE',
  LEVEL_UP: 'LEVEL_UP',
  XP_REWARD: 'XP_REWARD',
  ROUND_COMPLETE: 'ROUND_COMPLETE',
};

// DB buddy slugs are lowercase; all buddy art (figurines included, as of
// BP-BUDDY-ATLAS-VIEW-01) lives under /assets/buddies/{slug}/ with lowercase
// paths. Keep this list in sync with the buddies.
const SLUGS = ['ling', 'ming', 'feng', 'yong', 'qi', 'meng'];

// Build the per-buddy slot table. CELEBRATE / ENCOURAGE / LEVEL_UP / XP_REWARD
// each ship with 2 poses at launch; ROUND_COMPLETE shares CELEBRATE art (same
// paths) so the two slots can diverge later with zero code change. FIGURINE_VIEW_1
// (back-left, default) / FIGURINE_VIEW_2 (back-right) are the two map walk poses.
function buildBuddy(slug) {
  const base = `/assets/buddies/${slug}`;
  const celebrate = [`${base}/celebrate-1.webp`, `${base}/celebrate-2.webp`];
  return {
    [SLOT.AVATAR]: [`${base}/avatar-1.webp`],
    [SLOT.FIGURINE_VIEW_1]: [`${base}/figurine-1.webp`],
    [SLOT.FIGURINE_VIEW_2]: [`${base}/figurine-2.webp`],
    [SLOT.CELEBRATE]: celebrate,
    [SLOT.ENCOURAGE]: [`${base}/encourage-1.webp`, `${base}/encourage-2.webp`],
    [SLOT.LEVEL_UP]: [`${base}/level-up-1.webp`, `${base}/level-up-2.webp`],
    [SLOT.XP_REWARD]: [`${base}/xp-reward-1.webp`, `${base}/xp-reward-2.webp`],
    // ROUND_COMPLETE shares CELEBRATE art at launch (separate slot, same paths).
    [SLOT.ROUND_COMPLETE]: celebrate,
  };
}

export const MANIFEST = SLUGS.reduce((acc, slug) => {
  acc[slug] = buildBuddy(slug);
  return acc;
}, {});

// Unknown slugs fall back to ling — matches buddyAssets.js behaviour so a bad
// slug never 404s a whole surface.
function resolveSlug(slug) {
  const key = String(slug || '').toLowerCase();
  return key in MANIFEST ? key : 'ling';
}

function slotArray(slug, slot) {
  const buddy = MANIFEST[resolveSlug(slug)];
  return buddy[slot] || [];
}

// Single path — always the first entry. Deterministic; use for tests or any
// slot that holds exactly one asset (AVATAR, FIGURINE_*).
export function getBuddyAsset(slug, slot) {
  return slotArray(slug, slot)[0] || '';
}

// Full array for a slot.
export function getBuddyAssets(slug, slot) {
  return slotArray(slug, slot);
}

// Primary accessor for rotating slots: 1 image → always that image;
// 2+ → random pick. Returns '' if the slot is empty.
export function getRandomBuddyAsset(slug, slot) {
  const arr = slotArray(slug, slot);
  if (arr.length === 0) return '';
  if (arr.length === 1) return arr[0];
  return arr[Math.floor(Math.random() * arr.length)];
}
